import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Chunk } from '../../chunk.dto';

interface TableData {
  headers: string[];
  rows: string[][];
}

@Component({
  selector: 'app-table-chunk-displayer',
  imports: [TranslateModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (tableData()) {
      <div class="custom-scrollbar w-full overflow-x-auto my-3">
        <table class="w-full min-w-[400px] table-fixed rounded-[var(--skz-radius-low)] overflow-hidden">
          <thead>
            <tr>
              @for (header of tableData()!.headers; track $index; let last = $last) {
                <th
                  class="text-left px-4 py-3 text-[13px] font-bold text-[var(--skz-text-primary)] bg-[#e0e0e0] border-b border-[#eee]"
                  [class.border-r]="!last"
                  [class.border-r-[#eee]]="!last"
                >
                  {{ header }}
                </th>
              }
            </tr>
          </thead>
          <tbody>
            @for (row of tableData()!.rows; track $index; let lastRow = $last) {
              <tr class="hover:bg-gray-50 transition-colors duration-150">
                @for (cell of row; track $index; let first = $first; let lastCol = $last) {
                  <td
                    class="px-4 py-4 align-top text-sm text-[var(--skz-text-primary)]"
                    [class.font-semibold]="first"
                    [class.border-b]="!lastRow"
                    [class.border-b-[#eee]]="!lastRow"
                    [class.border-r]="!lastCol"
                    [class.border-r-[#eee]]="!lastCol"
                  >
                    {{ cell }}
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <div class="flex flex-col items-center gap-2 p-8 text-[var(--skz-text-muted)]">
        <mat-icon class="!text-[32px] !w-[32px] !h-[32px] opacity-40">table_chart</mat-icon>
        <span class="text-sm italic">{{ 'Chunk.Table.EmptyState' | translate }}</span>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: block;
      }
      table {
        border-collapse: separate !important;
        border-spacing: 0 !important;
        border: 1px solid #eee !important;
      }
      th,
      td {
        border-top: none !important;
        border-left: none !important;
        border-right: 1px solid #eee !important;
        border-bottom: 1px solid #eee !important;
        height: 39px;
      }
      th:last-child,
      td:last-child {
        border-right: none !important;
      }
      tr:last-child td {
        border-bottom: none !important;
      }
    `,
  ],
})
export class TableChunkDisplayerComponent {
  chunk = input.required<Chunk>();
  private translate = inject(TranslateService);

  tableData = computed<TableData | null>(() => {
    const chunk = this.chunk();
    if (!chunk || !chunk.editions) return null;

    const currentLang = this.translate.currentLang;
    const edition = chunk.editions.find((e: any) => e.language === currentLang) || chunk.editions[0];

    if (!edition || !edition.content) return null;

    try {
      const parsed = typeof edition.content === 'string' ? JSON.parse(edition.content) : edition.content;
      if (parsed && parsed.headers && parsed.rows) {
        return parsed as TableData;
      }
      return null;
    } catch {
      return null;
    }
  });
}
